<template>

  <!-- BODY -->
  <validation-observer
    #default="{ handleSubmit, invalid }"
    ref="refFormObserver"
  >
    <!-- Form -->
    <b-form
      class="p-2"
      @submit.prevent="handleSubmit(onSubmit)"
      @reset.prevent="resetForm"
    >

      <template
        v-if="section === 'specifics'"
      >
        <!-- Quality -->
        <validation-provider
          #default="validationContext"
          name="Quality"
          rules="required"
        >
          <b-form-group
            label="Quality *"
            label-for="quality"
            :state="getValidationState(validationContext)"
          >
            <b-form-select
              v-model="materialData.specifics.quality"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="qualityOptions"
              input-id="quality"
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
      </template>

      <template
        v-if="section === 'dimensions'"
      >
        <!-- Width -->
        <b-form-group
          label="Width (Print Rapport size)"
          label-for="width"
        >
          <b-input-group>
            <b-form-input
              id="width"
              v-model="materialData.dimensions.width"
              trim
            />
            <b-input-group-append>
              <b-form-select
                v-model="materialData.dimensions.width_units"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="dimensionOptions"
                input-id="width-units"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Length -->
        <b-form-group
          label="Length (Print Rapport size)"
          label-for="length"
        >
          <b-input-group>
            <b-form-input
              id="length"
              v-model="materialData.dimensions.length"
              trim
            />
            <b-input-group-append>
              <b-form-select
                v-model="materialData.dimensions.length_units"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="dimensionOptions"
                input-id="length-units"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </template>

      <template
        v-if="section === 'color'"
      >
        <!-- Silk Base Color -->
        <b-form-group
          label="Silk Base Color"
          label-for="silk-base-color"
        >
          <v-select
            v-model="materialData.colors"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="colorOptions"
            :reduce="val => val.value"
            multiple
            :clearable="false"
            input-id="silk-base-color"
          >
            <template v-slot:option="option">
              <span
                :style="{backgroundColor: option.color,
                         marginRight: '5px', width: '20px', height:'20px',
                         borderRadius: '4px', display:'inline-block'}"
              /><span>{{ option.label }}</span>
            </template>
          </v-select>
        </b-form-group>

        <!-- Print Colors -->
        <b-form-group
          label="Print Colors"
          label-for="print-colors"
        >
          <v-select
            v-model="materialData.other_colors"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="colorOptions"
            :reduce="val => val.value"
            multiple
            :clearable="false"
            input-id="print-colors"
          >
            <template v-slot:option="option">
              <span
                :style="{backgroundColor: option.color,
                         marginRight: '5px', width: '20px', height:'20px',
                         borderRadius: '4px', display:'inline-block'}"
              /><span>{{ option.label }}</span>
            </template>
          </v-select>
        </b-form-group>
      </template>

      <template
        v-if="section === 'images'"
      >
        <!-- Image -->
        <style-media-grid-form
          :style-media="styleMedia"
          :submit-handler="submitHandler"
          :fetch-style-material-media="fetchStyleMaterialMedia"
        />
      </template>

      <template
        v-if="section === 'sourcing'"
      >
        <!-- Reference -->
        <b-form-group
          label="Ref"
          label-for="ref"
        >
          <b-form-input
            id="ref"
            v-model="materialData.ref"
            trim
          />
        </b-form-group>

        <!-- Supplier -->
        <b-form-group
          label="Supplier"
          label-for="supplier"
        >
          <b-form-select
            v-model="materialData.supplier"
            :options="supplierOptions"
            input-id="supplier"
          />
        </b-form-group>

        <!-- Price -->
        <b-form-group
          label="Price"
          label-for="price"
        >
          <b-form-input
            id="price"
            v-model="materialData.price"
            trim
            placeholder="0.00"
          />
        </b-form-group>
      </template>

      <template
        v-if="section === 'consumption'"
      >
        <!-- Quantity -->
        <validation-provider
          #default="validationContext"
          name="Quantity"
        >
          <b-form-group
            label="Quantity"
            label-for="quantity"
          >
            <b-input-group>
              <b-form-input
                id="quantity"
                v-model="materialData.consumption.quantity"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-input-group-append>
                <b-form-select
                  v-model="materialData.consumption.quantity_units"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="quantityUnitsOptions"
                  input-id="quantity-units"
                />
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback>
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
      </template>

      <template
        v-if="section === 'placement'"
      >
        <!-- Placement -->
        <b-form-group
          label="Placement"
          label-for="placement"
        >
          <b-form-textarea
            id="placement"
            v-model="materialData.placement"
            trim
            rows="3"
            max-rows="5"
          />
        </b-form-group>
      </template>

      <!-- Form Actions -->
      <div class="d-flex mt-2">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="mr-2"
          type="submit"
          :disabled="invalid"
        >
          Next
        </b-button>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import _ from 'lodash'
import {
  BButton, BForm, BFormGroup, BFormInput,
  BFormInvalidFeedback, BFormSelect, BFormTextarea,
  BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, watch } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import router from '@/router'

import StyleMediaGridForm from './StyleMediaGridForm.vue'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,

    StyleMediaGridForm,
  },
  directives: {
    Ripple,
  },
  props: {
    colorOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    supplierOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    section: {
      type: String,
      required: true,
    },
    submitHandler: {
      type: Function,
      required: false,
      default: null,
    },
    fetchStyleMaterialMedia: {
      type: Function,
      required: false,
      default: null,
    },
    styleMedia: {
      type: Array,
      required: false,
      default: () => [],
    },
    initData: {
      type: Object,
      required: false,
      default: () => ({
        specifics: {}, dimensions: {}, consumption: {},
      }),
    },
  },
  data() {
    return {
      required,
    }
  },
  setup(props) {
    const qualityOptions = [
      'Silk Paper',
      'Kraft Paper',
      'Other',
    ]
    const dimensionOptions = [
      'mm',
      'cm',
    ]
    const quantityUnitsOptions = [
      'packaging',
    ]

    const materialDataInit = {
      specifics: {},
      consumption: {},
      dimensions: {},
    }
    const materialData = ref({ ...materialDataInit })

    const resetMaterialData = () => {
      materialData.value = { ...materialDataInit }
    }

    watch(() => props.initData, newValue => {
      if (newValue !== undefined) {
        materialData.value = _.cloneDeep(newValue)
      }
    }, { deep: true, immediate: true })

    const onSubmit = () => {
      const { section } = props
      let formData = {}

      switch (section) {
        case 'specifics':
        case 'dimensions':
        case 'consumption':
        case 'placement':
          formData = { [section]: materialData.value[section] }
          break
        case 'color':
          formData = {
            colors: materialData.value.colors,
            other_colors: materialData.value.other_colors,
          }
          break
        case 'sourcing':
          formData = {
            ref: materialData.value.ref,
            supplier: materialData.value.supplier,
            price: materialData.value.price,
          }
          break
        default:
          formData = {}
      }
      props.submitHandler({
        id: materialData.value.id || router.currentRoute.params.id,
        category_type: 'Paper',
        ...formData,
      })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetMaterialData)

    return {
      qualityOptions,
      quantityUnitsOptions,
      dimensionOptions,
      materialData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>
